import { Component, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { api_routes, db_tables, features, routes } from '@app/consts';
import { FeatureService } from '@app/core/services/feature.service';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { RowActionButtonInput } from '@app/modules/talent-track/talent-track-edit-employee/edit-employee/components/employment-records/components/employment-record-details/components/data-grid-comp/models/data-grid-comp.model';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { SecurityProtectedBase } from '@app/shared/components/security-protected/security-protected';
import { TranslateService } from '@ngx-translate/core';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { defer, forkJoin } from 'rxjs';
import { ConfirmDialogComponent } from '@app/shared/components/confirm-dialog/confirm-dialog.component';
import { GridState } from '@app/shared/services/grid-state/grid-state.model';
import { BuySellPlan } from '../models/buysell.model';
import { BuySellService } from '../services/buy-sell.service';

@Component({
  selector: 'app-buy-sell-plans',
  templateUrl: './buy-sell-plans.component.html',
  styleUrls: ['./buy-sell-plans.component.scss']
})
export class BuySellPlansComponent extends SecurityProtectedBase implements OnInit {
  public columns: any[] = [
    { field: "name", title: "Name", tableId: "tbl_BsPlanName", },
    { field: "description", title: "Description", type: "longText", tableId: "tbl_BsPlanDescription" },
    { field: "status", subField: "name", title: "Status", type: 'chip', chipValue: ['Published', null, 'Queued', 'Publishing'], tableId: "tbl_BsPlanPlanStatus" },
    { field: "created", subField: "by", title: "Created By", type: "employee", localizationCode: "CreatedBy" },
    { field: "created", subField: "on", title: "Created On", type: "dateTime", localizationCode: "CreatedOn" },
    // { field: "prorateOnFte", title: "Prorate On Fte", type: "booleanToggle" },
    // { field: "permitSellingNegativeNumbers", title: "Permit Selling Negative Numbers", type: "booleanToggle" },
    // { field: "displayFutureAccrual", title: "Display Future Accrual", type: "booleanToggle" },
    // { field: "maximumHoursEmployeeCanSell", title: "Maximum Hours Employee Can Sell", type: "number" },
    // { field: "maximumHoursEmployeeCanBuy", title: "Maximum Hours Employee Can Buy" },
    // { field: "timeOffType", subField: "name", title: "Time Off Type" },
    // { field: "payRateInterval", subField: "text", title: "Pay Rate Interval" },
    // { field: "hoursPerPeriod", title: "Hours Per Period" },
    // { field: "allowBuy", title: "Allow Buy", type: "booleanToggle" },
    // { field: "allowSell", title: "Allow Sell", type: "booleanToggle" },
  ];

  filterCategories: any[] = [
    { field: "name", title: "Name", tableId: "tbl_BsPlanName", type: "String", dataType: 'String' },
    { field: "description", title: "Description", tableId: "tbl_BsPlanDescription", type: "String", dataType: 'String' },
    { field: "status.name", title: "Status", tableId: "tbl_BsPlanPlanStatus", type: "String", dataType: 'String' },
    { field: "prorateOnFte", title: "Prorate On Fte", tableId: "tbl_BsPlanProrateOnFte", type: "Boolean", dataType: 'Boolean' },
    { field: "permitSellingNegativeNumbers", title: "Permit Selling Negative Numbers", tableId: "tbl_BsPlanPermitSellingNegativeNumbers", type: "Boolean", dataType: 'Boolean' },
    { field: "displayFutureAccrual", title: "Display Future Accrual", tableId: "tbl_BsPlanDisplayFutureAccrual", type: "Boolean", dataType: 'Boolean' },
    { field: "maximumHoursEmployeeCanSell", title: "Maximum Hours Employee Can Sell", tableId: "tbl_BsPlanMaximumHoursEmployeeCanSell", type: "Number", dataType: 'Number' },
    { field: "maximumHoursEmployeeCanBuy", title: "Maximum Hours Employee Can Buy", tableId: "tbl_BsPlanMaximumHoursEmployeeCanBuy", type: "Number", dataType: 'Number' },
    { field: "timeOffType.name", title: "Time Off Type", tableId: "tbl_BsPlanTimeOffType", type: "String", dataType: 'String' },
    { field: "payRateInterval.text", title: "Pay Rate Interval", tableId: "tbl_BsPlanPayRateInterval", type: "String", dataType: 'String' },
    { field: "hoursPerPeriod", title: "Hours Per Period", tableId: "tbl_BsPlanHoursPerPeriod", type: "Number", dataType: 'Number' },
    { field: "allowBuy", title: "Allow Buy", tableId: "tbl_BsPlanAllowBuy", type: "Boolean", dataType: 'Boolean' },
    { field: "allowSell", title: "Allow Sell", tableId: "tbl_BsPlanAllowSell", type: "Boolean", dataType: 'Boolean' },
  ];

  sortableColumns: any[] = [
    { field: "name", sortValue: "name" },
    { field: "description", sortValue: "description" },
    { field: "status", sortValue: "status.name" },
    { field: "prorateOnFte", sortValue: "prorateOnFte" },
    { field: "permitSellingNegativeNumbers", sortValue: "permitSellingNegativeNumbers" },
    { field: "displayFutureAccrual", sortValue: "displayFutureAccrual" },
    { field: "maximumHoursEmployeeCanSell", sortValue: "maximumHoursEmployeeCanSell" },
    { field: "maximumHoursEmployeeCanBuy", sortValue: "maximumHoursEmployeeCanBuy" },
    { field: "timeOffType", sortValue: "timeOffType.name" },
    { field: "payRateInterval", sortValue: "payRateInterval.text" },
    { field: "hoursPerPeriod", sortValue: "hoursPerPeriod" },
    { field: "allowBuy", sortValue: "allowBuy" },
    { field: "allowSell", sortValue: "allowSell" }
  ];

  public gridDataResult: GridDataResult;
  public api_routes = api_routes;
  public db_tables = db_tables;
  isLoading: boolean = true;
  pageSize: number = 20;
  skip: number = 0;
  clearSelectedItems: boolean = false;
  dialogRef: any;
  formValid: any;
  sortString: string = 'version.createdOn-desc';
  isLoadingPermissions: boolean = true;
  filterString: string;
  gridId: string = 'buySellPlans';
  tableId: string = db_tables.BuySellPlans;
  getRequest: any;
  searchValue: string;
  searchFilterString: string;
  gridActionButtons: RowActionButtonInput[] = [
    {
      id: 'copy',
      name: this.translate.instant('Copy'),
      icon: 'content_copy'
    }
  ];

  constructor(
    private overlayService: OverlayService,
    public translate: TranslateService,
    private snackbarService: SnackbarService,
    private router: Router,
    private dialog: MatDialog,
    private buySellService: BuySellService,
    featureService: FeatureService
  ) {
    super(featureService, features.BUY_SELL);
  }

  ngOnInit(): void {
    if (this.hasFeatureAccess) {
      this.getBuySellPlans();
    }
  }

  getBuySellPlans() {
    this.isLoading = true;

    // Combine filterString and searchFilterString if both exist
    let combinedFilter = '';
    if (this.filterString && this.searchFilterString) {
      combinedFilter = `(${this.filterString}) AND (${this.searchFilterString})`;
    } else if (this.filterString) {
      combinedFilter = this.filterString;
    } else if (this.searchFilterString) {
      combinedFilter = this.searchFilterString;
    } else {
      combinedFilter = '';
    }
    // Use combinedFilter in the service call

    this.getRequest = this.buySellService.getBuySellPlans(this.pageSize, this.skip, combinedFilter, this.sortString)
      .subscribe(
        {
          next: (v) => {
            this.gridDataResult = {
              data: v.data,
              total: v.total
            };
            this.isLoading = false;
          },
          error: (e) => this.isLoading = false,
          complete: () => this.isLoading = false
        }
      );
  }

  editOrCreateBuySellPlan(buySellPlan?: BuySellPlan) {
    if (buySellPlan?.id) {
      this.router.navigate([`TimeOff${routes.BUY_SELL}${routes.BUY_SELL_PLANS}${routes.EDITOR}/${buySellPlan?.id}`]);
    }
    else {
      this.router.navigate([`TimeOff${routes.BUY_SELL}${routes.BUY_SELL_PLANS}${routes.EDITOR}`]);
    }

  }

  openConfirmDeleteDialog(importIds: string[]) {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      text: this.translate.instant('AreYouSureYouWantToDeleteThis')
    };

    const dialogRef = this.dialog.open(ConfirmDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(
      data => {
        if (data === true) {
          this.deleteBuySellPlans(importIds);
        }
      }
    );
  }

  deleteBuySellPlans(buySellPlanIds: string[]) {
    const observables = buySellPlanIds.map(selectedItem => defer(() => this.buySellService.deleteBuySellPlan(selectedItem)));

    this.overlayService.show();

    forkJoin(observables)
      .subscribe(
        {
          next: (v) => {
            this.clearSelectedItems = !this.clearSelectedItems;
            this.snackbarService.openSnackBar(`${this.translate.instant('DeletedSuccessfully')}`, 'clear', 'success');
            this.getBuySellPlans();
          },
          error: (e) => this.overlayService.hide(),
          complete: () => this.overlayService.hide()
        }
      );
  }

  gridStateChange(gridState: GridState) {
    if (this.pageSize !== gridState.pageSize) {
      this.pageSize = gridState.pageSize;
    }
    if (this.skip !== gridState.skip) {
      this.skip = gridState.skip;
    }
    if (this.sortString !== gridState.sortString) {
      this.sortString = gridState.sortString;
    }
    if (this.filterString !== gridState.filter) {
      this.filterString = gridState.filter;
      this.skip = 0;
    }

    if (this.searchValue !== gridState.searchValue) {
      let variants = [];
      this.searchFilterString = '';
      this.searchValue = gridState.searchValue;

      if (this.searchValue !== null) {
        variants = this.searchValue.split(' ').filter(value => value);
      }

      variants.map(variant => {
        if (variant) {
          if (this.searchFilterString.length > 2) {
            this.searchFilterString += ' AND ';
          }
          this.searchFilterString += `(Name like "${variant}")`;
        }
      });

      this.skip = 0;
    }

    this.getRequest.unsubscribe();
    this.getBuySellPlans();
  }

  copy(event) {
    let buySellPlan = event.rowData;
    this.overlayService.show('Copying buy sell plan...');

    this.buySellService.copyBuySellPlan(buySellPlan.id)
      .subscribe(
        {
          next: (v) => {
            this.snackbarService.openSnackBar(`${this.translate.instant('CopiedSuccessfully')}`, 'clear', 'success');
            this.router.navigate([`${routes.TIME_OFF}${routes.BUY_SELL}${routes.BUY_SELL_PLANS}${routes.EDITOR}/${v.id}`]);
          },
          error: (e) => this.overlayService.hide(),
          complete: () => this.overlayService.hide()
        }
      );
  }

}
